import {IRoomService} from '@techsee/techsee-client-infra/lib/services/RoomService';
import {AppState} from '../../../../../app.state';
// @ts-ignore
import {findGuidInUrl} from '@techsee/techsee-common/lib/utils';
import {SESSION_SOURCE, UserType} from '@techsee/techsee-common/lib/constants/room.constants';
import {observable, action, computed, reaction} from 'mobx';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import includes from 'lodash/includes';
import filter from 'lodash/filter';
import some from 'lodash/some';
import tsDate from '@techsee/techsee-date';
import {ITranslate} from '../../../../../services/LocalizationService';
import {IInviteFlowManager} from '../../../_contracts/InviteContracts';
import {PlatformType} from '@techsee/techsee-common/lib/constants/utils.constant';
import {GeneralSettings, InviteMethodSettings} from '../../../../../models/AccountSettings';
import {IAccountSocketService} from '../../../../../services/AccountSocket/AccountSocketService';
import {IGroup} from '../../../../../services/AccountSocket/AccountSocketServiceContracts';

export interface IRoom {
    _id: string;
    customerId: string;
    customerNumber: string;
    guids: string[];
    sentURL: string;
    sessionCode: string;
    creationTimestamp: string;
    connectedTimestamp: string;
    lastAgentAction: string;
    lastClientAction: string;
    lastUpdate: string;
    clientType: PlatformType;
    sessionSource: SESSION_SOURCE;
    groups: IGroup[];
}

export interface IOfflineSession {
    room: IRoom;
    lastActionBy: UserType;
    createdBy: string;
    occupied: boolean;
}

export interface IOfflineSessionsController {
    readonly offlineSessions: IOfflineSession[];
    readonly selectedSession: IOfflineSession;
    readonly translate: ITranslate;
    readonly isJoinAvailable: boolean;

    isSessionSelected(session: IOfflineSession): boolean;
    selectSession(session: IOfflineSession): void;
    onJoin(callback: (sessionId: string) => void): void;
}

export class OfflineSessionsController implements IOfflineSessionsController {
    private _selectedGroupsForOfflineAlerts: string[];

    @observable private _selectedSession: IOfflineSession = {room: {}} as any as IOfflineSession;

    constructor(
        private _flowManager: IInviteFlowManager,
        public translate: ITranslate,
        private _roomService: IRoomService,
        private _appState: AppState,
        private _accSettings: GeneralSettings,
        private _inviteSettings: InviteMethodSettings,
        private _currentUser: any,
        private _accountSocketService: IAccountSocketService
    ) {
        this.isSessionSelected = this.isSessionSelected.bind(this);
        this.selectSession = this.selectSession.bind(this);
        this.onJoin = this.onJoin.bind(this);

        this._selectedGroupsForOfflineAlerts = this._inviteSettings
            ? this._inviteSettings.selectedGroupsForOfflineAlerts
            : [];
        const userGroups = this._currentUser.groups || [];

        const isUserPermittedToGetOfflineAlerts =
            this._selectedGroupsForOfflineAlerts.length && this._inviteSettings.showAlertsToSelectedGroups
                ? this._selectedGroupsForOfflineAlerts.some((group) => userGroups.includes(group))
                : true;

        if (isUserPermittedToGetOfflineAlerts) {
            reaction(
                () => this._accountSocketService.pendingRooms.slice(),
                (rooms) => {
                    this.updateOfflineRoomCount(rooms);
                },
                {fireImmediately: true}
            );
        }
    }

    @computed
    get offlineSessions() {
        const sessions = (this._accountSocketService.pendingRooms as IOfflineSession[]).map((session) => ({
            ...session,
            room: {
                ...session.room,
                sessionCode: this.getSessionCode(session.room),
                creationTimestamp: this.dateFormat(session.room.creationTimestamp),
                connectedTimestamp: this.dateFormat(session.room.connectedTimestamp),
                lastUpdate: this.dateFormat(
                    session.lastActionBy === UserType.dashboard
                        ? session.room.lastAgentAction
                        : session.room.lastClientAction
                )
            }
        }));

        if (this._inviteSettings.showAlertsToSelectedGroups && this._selectedGroupsForOfflineAlerts) {
            sessions.filter((roomData: IOfflineSession) =>
                some(roomData.room.groups, (group: IGroup) => includes(this._selectedGroupsForOfflineAlerts, group._id))
            );
        }

        return orderBy(this._filterSessionsByActiveSessions(sessions), ({room}) => room.lastUpdate, 'desc');
    }

    @computed
    get isJoinAvailable() {
        return !isEmpty(this._selectedSession.room);
    }

    @computed
    get selectedSession() {
        return this._selectedSession;
    }

    isSessionSelected(session: IOfflineSession) {
        return this._selectedSession.room._id === session.room._id;
    }

    @action
    selectSession(session: IOfflineSession) {
        if (session.occupied) {
            return;
        }

        this._selectedSession = session;
    }

    @action
    onJoin(callback: (sessionId: string) => void) {
        callback(this._selectedSession.room.sessionCode);
    }

    private getSessionCode(room: IRoom) {
        const code = isEmpty(room.guids) ? findGuidInUrl(room.sentURL) : room.guids[0];

        if (room.clientType === PlatformType.dynamicAppClient) {
            return code.substr(this._accSettings.guidPrefix.length);
        }

        return code;
    }

    private dateFormat(date: string) {
        return tsDate(date).format('DD/MM/YYYY HH:mm:ss');
    }

    private updateOfflineRoomCount(offlineSessions: IOfflineSession[]) {
        let relevantOfflineRooms = offlineSessions;

        if (this._inviteSettings.showAlertsToSelectedGroups && this._selectedGroupsForOfflineAlerts) {
            relevantOfflineRooms = offlineSessions.filter((roomData: IOfflineSession) =>
                some(roomData.room.groups, (group: IGroup) => includes(this._selectedGroupsForOfflineAlerts, group._id))
            );
        }

        const filtered = this._filterSessionsByActiveSessions(relevantOfflineRooms);

        this._appState.setOfflineRoomCount(filtered.length);
    }

    private _filterSessionsByActiveSessions(offlineSessions: IOfflineSession[]) {
        return filter(offlineSessions, (session) =>
            includes([UserType.dashboard, UserType.client], session.lastActionBy)
        );
    }
}
